// Pull "what is true about this project right now" out of the live
// graph so the FLUX render is grounded in real Intent / Style /
// Materials / SKUs — not a generic Pinterest prompt. Every chunk we
// inject carries a citation back to the graph node it came from, so
// the Why panel can trace any rendered pixel to a graph fact.

import { SAMPLE_PROJECT, nodeById } from "../data/sampleGraph";
import type { GraphNode } from "../types/ontology";
import type { FluxRoomCue, FluxStyle } from "../data/generated/flux-prompts.generated";
import { FLUX_ROOM_CUES, FLUX_STYLES } from "../data/generated/flux-prompts.generated";

export interface ProjectGroundingChip {
  id: string;
  kind: GraphNode["kind"];
  label: string;
}

export interface ProjectRoomBinding {
  /** Graph room node (e.g. r1 "Living"). */
  room: GraphNode;
  /** Best-matching FLUX room cue, or null when no mapping exists. */
  cue: FluxRoomCue | null;
}

export interface ProjectRenderContext {
  project: GraphNode | null;
  intent: GraphNode | null;
  styles: readonly GraphNode[];
  materials: readonly GraphNode[];
  rooms: readonly ProjectRoomBinding[];
  /** SKUs grouped by room (for the "include in render" hint chips). */
  skusByRoom: Readonly<Record<string, readonly GraphNode[]>>;
  /** FLUX style we recommend for this project (mapped from the dominant Style node). */
  recommendedStyle: FluxStyle;
}

/* room-label → flux-cue mapping. Room labels in the graph are
   human-readable ("Master Bedroom"); FLUX cues are kebab-case. We
   normalise both sides before matching so editing the seed data
   doesn't break the mapping. */
const ROOM_LABEL_TO_CUE_KIND: ReadonlyArray<readonly [RegExp, string]> = [
  [/master\s*bed/i,    "master-bedroom"],
  [/kid|child/i,       "kids-bedroom"],
  [/kitchen/i,         "kitchen"],
  [/living|hall/i,     "living"],
  [/puja|prayer/i,     "puja-room"],
  [/din(e|ing)/i,      "dining"],
];

function matchRoomToCue(label: string): FluxRoomCue | null {
  for (const [rx, kind] of ROOM_LABEL_TO_CUE_KIND) {
    if (rx.test(label)) {
      const cue = FLUX_ROOM_CUES.find((c) => c.kind === kind);
      if (cue) return cue;
    }
  }
  return null;
}

/* Style-node label → FLUX style id. Falls back to scandi-indo when
   the graph's style doesn't map cleanly — that's our house default. */
function pickFluxStyle(styleNodes: readonly GraphNode[]): FluxStyle {
  const labels = styleNodes.map((n) => n.label.toLowerCase()).join(" ");
  if (/scandi|indo/.test(labels))            return FLUX_STYLES.find((s) => s.id === "scandi-indo")            ?? FLUX_STYLES[0]!;
  if (/mughal|traditional/.test(labels))     return FLUX_STYLES.find((s) => s.id === "indian-traditional")     ?? FLUX_STYLES[0]!;
  if (/japandi|japan/.test(labels))          return FLUX_STYLES.find((s) => s.id === "japandi")                ?? FLUX_STYLES[0]!;
  if (/industrial|loft/.test(labels))        return FLUX_STYLES.find((s) => s.id === "industrial-loft")        ?? FLUX_STYLES[0]!;
  if (/modern|minimal/.test(labels))         return FLUX_STYLES.find((s) => s.id === "modern-minimal")         ?? FLUX_STYLES[0]!;
  return FLUX_STYLES[0]!;
}

export function readProjectRenderContext(): ProjectRenderContext {
  const project   = SAMPLE_PROJECT.nodes.find((n) => n.kind === "Project")  ?? null;
  const intent    = SAMPLE_PROJECT.nodes.find((n) => n.kind === "Intent")   ?? null;
  const styles    = SAMPLE_PROJECT.nodes.filter((n) => n.kind === "Style");
  const materials = SAMPLE_PROJECT.nodes.filter((n) => n.kind === "Material");
  const roomNodes = SAMPLE_PROJECT.nodes.filter((n) => n.kind === "Room");

  // SKUs grouped by room — walks IN_ROOM / FITS_IN edges from the graph.
  const skusByRoom: Record<string, GraphNode[]> = {};
  for (const r of roomNodes) skusByRoom[r.id] = [];
  for (const e of SAMPLE_PROJECT.edges) {
    if (e.kind !== "IN_ROOM" && e.kind !== "FOR_ROOM") continue;
    const sku  = nodeById.get(e.source);
    const room = nodeById.get(e.target);
    if (!sku || sku.kind !== "SKU") continue;
    if (!room || room.kind !== "Room") continue;
    (skusByRoom[room.id] ??= []).push(sku);
  }
  // Fallback: if no IN_ROOM edges exist, every SKU is "project-wide".
  if (Object.values(skusByRoom).every((a) => a.length === 0)) {
    const allSkus = SAMPLE_PROJECT.nodes.filter((n) => n.kind === "SKU");
    for (const r of roomNodes) skusByRoom[r.id] = allSkus;
  }

  const rooms: ProjectRoomBinding[] = roomNodes.map((room) => ({
    room,
    cue: matchRoomToCue(room.label),
  }));

  return {
    project,
    intent,
    styles,
    materials,
    rooms,
    skusByRoom,
    recommendedStyle: pickFluxStyle(styles),
  };
}

/* Compose the "grounded fragment" that gets prepended to the user's
   free text. This is what makes the render Foyr's project, not a
   generic Scandi room. */
export function composeGroundedFragment(opts: {
  ctx: ProjectRenderContext;
  roomId: string | null;
  includeSkus: boolean;
}): string {
  const parts: string[] = [];
  if (opts.ctx.intent) parts.push(`intent: ${opts.ctx.intent.label.toLowerCase()}`);
  if (opts.ctx.materials.length > 0) {
    const mats = opts.ctx.materials.map((m) => m.label.toLowerCase()).join(", ");
    parts.push(`materials: ${mats}`);
  }
  if (opts.roomId && opts.includeSkus) {
    const skus = opts.ctx.skusByRoom[opts.roomId] ?? [];
    if (skus.length > 0) {
      const skuList = skus.slice(0, 5).map((s) => s.label.split("·")[0]!.trim().toLowerCase()).join(", ");
      parts.push(`features: ${skuList}`);
    }
  }
  return parts.length === 0 ? "" : parts.join("; ") + ".";
}

/* Build a citation trace — designer can hover to see exactly which
   graph nodes shaped the prompt. The Why panel can walk this. */
export interface GroundingCitation {
  fragment: string;
  fromNodeId: string;
  fromKind: GraphNode["kind"];
}

/* Dynamic "innovative twist" suggestions, built per-room from the
   graph. Each suggestion is grounded — it references a real Material
   / SKU / Intent / Vendor from the project, paired with a creative
   environmental twist (time of day, weather, occasion, life-moment)
   so the designer gets *ideas*, not stock prompts. */

const TIME_TWISTS  = ["morning chai light", "golden-hour low sun", "rainy afternoon, soft greys", "blue-hour just after sunset", "moody candle-only night"] as const;
const LIFE_TWISTS  = ["a child reading on the rug", "an open cookbook on the island", "guests gathered around the table", "monsoon plants by the window", "a Diwali marigold garland over the door"] as const;
const MOOD_TWISTS  = ["cinematic, AD-Magazine cover", "soft documentary realism", "editorial wide shot, golden ratio", "intimate close-up framing", "drone-style high-angle wide"] as const;

export interface PromptSuggestion {
  /** Short label shown on the chip. */
  label: string;
  /** The actual text that gets injected into "extra details". */
  fragment: string;
  /** Graph nodes this suggestion was built from — for citations. */
  fromNodeIds: readonly string[];
}

const ROOM_KIND_FROM_LABEL = (label: string): string => {
  const lower = label.toLowerCase();
  if (lower.includes("kitchen")) return "kitchen";
  if (lower.includes("kids"))    return "kids";
  if (lower.includes("master"))  return "master";
  if (lower.includes("living"))  return "living";
  return "room";
};

export function suggestPrompts(opts: {
  ctx: ProjectRenderContext;
  roomId: string | null;
  count?: number;
}): readonly PromptSuggestion[] {
  const { ctx, roomId } = opts;
  const max = opts.count ?? 5;
  const room = ctx.rooms.find((r) => r.room.id === roomId);
  if (!room) return [];
  const kind = ROOM_KIND_FROM_LABEL(room.room.label);
  const skus = ctx.skusByRoom[room.room.id] ?? [];
  const mat  = ctx.materials.map((m) => m.label.toLowerCase());
  const has  = (rx: RegExp): boolean => mat.some((m) => rx.test(m)) || skus.some((s) => rx.test(s.label.toLowerCase()));

  const out: PromptSuggestion[] = [];

  // 1. Material-led twist — pair the dominant material with a moment.
  if (has(/oak|wood/)) {
    out.push({
      label: "oak warm at golden hour",
      fragment: "oak surfaces catching golden-hour light, dust motes in the sunbeam, lived-in warmth",
      fromNodeIds: ctx.materials.filter((m) => /oak|wood/i.test(m.label)).map((m) => m.id),
    });
  }
  if (has(/brass/)) {
    out.push({
      label: "brushed brass at blue hour",
      fragment: "brushed brass details catching the cool blue-hour light from the window, deep contrast",
      fromNodeIds: ctx.materials.filter((m) => /brass/i.test(m.label)).map((m) => m.id),
    });
  }
  if (has(/quartz|stone|calacatta/)) {
    out.push({
      label: "calacatta veining, editorial",
      fragment: "close-up of calacatta veining, shallow depth of field, editorial styling",
      fromNodeIds: ctx.materials.filter((m) => /quartz|stone|calacatta/i.test(m.label)).map((m) => m.id),
    });
  }

  // 2. Room-led life moment.
  if (kind === "kitchen") {
    out.push({
      label: "open cookbook, fresh herbs",
      fragment: "open cookbook on the island, fresh herbs in ceramic pots, steam from a pan, late-morning light",
      fromNodeIds: skus.slice(0, 2).map((s) => s.id),
    });
  }
  if (kind === "living") {
    out.push({
      label: "Diwali marigolds, evening",
      fragment: "Diwali marigold garlands over the doorway, brass diyas on the coffee table, warm evening glow",
      fromNodeIds: ctx.styles.map((s) => s.id),
    });
  }
  if (kind === "kids") {
    out.push({
      label: "child reading on the rug",
      fragment: "a child reading on the rug, soft fairy lights, scattered books, late-afternoon sun, gentle wide shot",
      fromNodeIds: skus.slice(0, 2).map((s) => s.id),
    });
  }
  if (kind === "master") {
    out.push({
      label: "rain morning, layered linen",
      fragment: "rainy morning behind sheer curtains, layered linen bedding rumpled, steaming cup on the bedside",
      fromNodeIds: ctx.intent ? [ctx.intent.id] : [],
    });
  }

  // 3. Intent-led creative direction.
  if (ctx.intent) {
    out.push({
      label: `${ctx.intent.label} · cinematic`,
      fragment: `interpreted as: ${ctx.intent.label.toLowerCase()}, cinematic wide shot, AD-Magazine cover composition`,
      fromNodeIds: [ctx.intent.id],
    });
  }

  // 4. SKU-led hero shot — if there's a clear hero piece, feature it.
  const hero = skus.find((s) => /chandelier|pendant|island|sofa/i.test(s.label));
  if (hero) {
    out.push({
      label: `hero: ${hero.label.split("·")[0]!.trim().toLowerCase()}`,
      fragment: `${hero.label.split("·")[0]!.trim().toLowerCase()} as the hero element, framed as a magazine spread, the rest of the room out of focus`,
      fromNodeIds: [hero.id],
    });
  }

  // 5. Backup: pure mood/time picks that work for any room.
  out.push({
    label: `${TIME_TWISTS[0]}`,
    fragment: `${TIME_TWISTS[0]}, ${MOOD_TWISTS[0]}`,
    fromNodeIds: [],
  });
  out.push({
    label: `${LIFE_TWISTS[2]}`,
    fragment: `${LIFE_TWISTS[2]}, ${MOOD_TWISTS[1]}`,
    fromNodeIds: [],
  });

  // De-dupe by label, cap to max.
  const seen = new Set<string>();
  return out.filter((s) => (seen.has(s.label) ? false : (seen.add(s.label), true))).slice(0, max);
}

/* Client-mode prompts: emotional life-moment shots, written from the
   client's point of view. These use the actual Client node label
   (e.g. "Anika & Rohan") and the project deadline as anchors so the
   client sees "their home, lived in", not a stock interior. */
export function clientShowPrompts(opts: {
  ctx: ProjectRenderContext;
  roomId: string | null;
  count?: number;
}): readonly PromptSuggestion[] {
  const { ctx, roomId } = opts;
  const max = opts.count ?? 5;
  const room = ctx.rooms.find((r) => r.room.id === roomId);
  if (!room) return [];
  const kind = ROOM_KIND_FROM_LABEL(room.room.label);

  // Surface the Client node — for the personal moment.
  const clientLabel: string | null = (() => {
    const c = SAMPLE_PROJECT.nodes.find((n) => n.kind === "Client");
    return c ? c.label : null;
  })();
  const projectLabel = ctx.project?.label ?? "your home";

  const out: PromptSuggestion[] = [];

  if (kind === "living") {
    out.push({
      label: `${clientLabel ?? "the family"} hosting`,
      fragment: `${clientLabel ?? "the family"} hosting friends for an evening, lamps warm, conversation, ${projectLabel} feels lived-in and welcoming`,
      fromNodeIds: ctx.project ? [ctx.project.id] : [],
    });
    out.push({
      label: "Diwali first evening",
      fragment: `the first Diwali in ${projectLabel}, marigold garlands, brass diyas glowing, family silhouettes`,
      fromNodeIds: ctx.project ? [ctx.project.id] : [],
    });
  }
  if (kind === "kitchen") {
    out.push({
      label: "Sunday breakfast",
      fragment: `slow Sunday breakfast at the kitchen island, fresh chai, paratha on a plate, soft natural light`,
      fromNodeIds: [],
    });
  }
  if (kind === "master") {
    out.push({
      label: "first morning in the new home",
      fragment: `the first morning waking up in ${projectLabel}, soft linen tousled, sunlight through sheer curtains, a calm beginning`,
      fromNodeIds: ctx.project ? [ctx.project.id] : [],
    });
  }
  if (kind === "kids") {
    out.push({
      label: "child's birthday",
      fragment: `a child's birthday morning in ${projectLabel}, fairy lights, balloons, soft toys, parents in the doorway`,
      fromNodeIds: ctx.project ? [ctx.project.id] : [],
    });
  }

  // Universal closers — any room, emotional pull.
  out.push({
    label: "monsoon afternoon",
    fragment: `a monsoon afternoon, rain blurring the windows, ${projectLabel} feels safe and warm, a single lamp on, book open on the chair`,
    fromNodeIds: [],
  });
  out.push({
    label: "moving-in day",
    fragment: `the day ${clientLabel ?? "the family"} moved into ${projectLabel}, boxes mostly unpacked, a kettle steaming, photos finally on the wall`,
    fromNodeIds: ctx.project ? [ctx.project.id] : [],
  });

  const seen = new Set<string>();
  return out.filter((s) => (seen.has(s.label) ? false : (seen.add(s.label), true))).slice(0, max);
}

export function citationsFor(ctx: ProjectRenderContext, roomId: string | null): readonly GroundingCitation[] {
  const out: GroundingCitation[] = [];
  if (ctx.intent)  out.push({ fragment: ctx.intent.label,  fromNodeId: ctx.intent.id, fromKind: "Intent" });
  for (const s of ctx.styles)    out.push({ fragment: s.label, fromNodeId: s.id, fromKind: "Style" });
  for (const m of ctx.materials) out.push({ fragment: m.label, fromNodeId: m.id, fromKind: "Material" });
  if (roomId) {
    const skus = ctx.skusByRoom[roomId] ?? [];
    for (const s of skus.slice(0, 5)) out.push({ fragment: s.label, fromNodeId: s.id, fromKind: "SKU" });
  }
  return out;
}

